import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LEGAL, LEGAL_PAGES, esDatoPendiente } from '../../config/legalConfig';

const enlaceTexto = 'font-medium text-slate-900 underline decoration-slate-300 underline-offset-[3px] transition-colors hover:decoration-slate-900 dark:text-white dark:decoration-slate-600 dark:hover:decoration-white';

// Estilos de texto corrido del documento, aplicados una sola vez al contenedor.
const prosa = [
  'text-[15px] leading-7 text-slate-600 dark:text-slate-300',
  '[&>p]:max-w-[70ch] [&>ul]:max-w-[70ch]',
  '[&_p+p]:mt-3 [&_p+ul]:mt-3 [&_ul+p]:mt-3 [&_p+div]:mt-4 [&_div+p]:mt-4',
  '[&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5 [&_li]:pl-1 [&_li]:marker:text-slate-400 dark:[&_li]:marker:text-slate-600',
  '[&_strong]:font-semibold [&_strong]:text-slate-900 dark:[&_strong]:text-white',
].join(' ');

const prefiereMenosMovimiento = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const irASeccion = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: prefiereMenosMovimiento() ? 'auto' : 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${id}`);
};

// Dato del titular. Mientras siga siendo el texto de ejemplo se marca solo en
// desarrollo, para que se vea qué falta rellenar en legalConfig.js.
export const Dato = ({ campo }) => {
  const valor = LEGAL[campo];
  if (esDatoPendiente(valor)) {
    return process.env.NODE_ENV === 'production'
      ? <>{valor}</>
      : <span className="rounded-sm bg-yellow-400/15 px-1 font-mono text-[0.85em] text-yellow-700 outline-dashed outline-1 outline-yellow-500/60 dark:text-yellow-300">{valor}</span>;
  }
  if (campo === 'email') return <a href={`mailto:${valor}`} className={enlaceTexto}>{valor}</a>;
  return <>{valor}</>;
};

export const Enlace = ({ to, children }) => <Link to={to} className={enlaceTexto}>{children}</Link>;

export const EnlaceExterno = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={enlaceTexto}>{children}</a>
);

// Primera capa informativa (lo que la AEPD llama "información básica").
export const Resumen = ({ titulo = 'Lo esencial', items }) => (
  <aside aria-label={titulo} className="rounded-xl border border-slate-200 bg-slate-50/80 p-5 dark:border-white/10 dark:bg-white/[0.03]">
    <p className="text-sm font-semibold text-slate-900 dark:text-white">{titulo}</p>
    <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
      {items.map(([termino, descripcion]) => (
        <React.Fragment key={termino}>
          <dt className="font-medium text-slate-500 dark:text-slate-400">{termino}</dt>
          <dd className="text-slate-700 dark:text-slate-200">{descripcion}</dd>
        </React.Fragment>
      ))}
    </dl>
  </aside>
);

// Tabla que en móvil se convierte en fichas apiladas (cada celda lleva su
// etiqueta), sin scroll horizontal.
export const Filas = ({ etiqueta, columnas, filas }) => (
  <div role="table" aria-label={etiqueta} className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 text-sm dark:divide-white/10 dark:border-white/10" style={{ '--cols': columnas.length }}>
    <div role="row" className="hidden bg-slate-50 px-4 py-2.5 font-semibold text-slate-900 dark:bg-white/[0.04] dark:text-white md:grid md:gap-6 md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]">
      {columnas.map((c) => <span role="columnheader" key={c}>{c}</span>)}
    </div>
    {filas.map((fila, i) => (
      <div role="row" key={i} className="grid gap-2 px-4 py-3.5 md:gap-6 md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]">
        {fila.map((celda, j) => (
          <div role="cell" key={j} className={j === 0 ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}>
            <span aria-hidden="true" className="mb-0.5 block text-xs font-medium text-slate-400 md:hidden">{columnas[j]}</span>
            {celda}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const useSeccionActiva = (ids) => {
  const [activa, setActiva] = useState(ids[0]);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entradas) => {
        const visible = entradas.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiva(visible.target.id);
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [ids]);
  return activa;
};

const LegalDocument = ({ titulo, intro, resumen, secciones }) => {
  const { hash } = useLocation();
  const ids = React.useMemo(() => secciones.map((s) => s.id), [secciones]);
  const activa = useSeccionActiva(ids);

  useEffect(() => {
    const anterior = document.title;
    document.title = `${titulo} · ${LEGAL.nombreSitio}`;
    return () => { document.title = anterior; };
  }, [titulo]);

  useEffect(() => {
    const id = hash.replace('#', '');
    if (id && document.getElementById(id)) irASeccion(id);
    else window.scrollTo(0, 0);
    // Solo al entrar en la página: los clics del índice ya gestionan el scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titulo]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-8 sm:px-6 lg:pt-12">
      <nav aria-label="Ruta de navegación" className="text-sm text-slate-500 dark:text-slate-400">
        <Link to="/" className="hover:text-slate-900 dark:hover:text-white">Inicio</Link>
        <span aria-hidden="true" className="mx-2 text-slate-300 dark:text-slate-600">/</span>
        <span>Información legal</span>
      </nav>

      <header className="mt-4 max-w-3xl">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl">{titulo}</h1>
        {intro && <p className="mt-3 text-base leading-7 text-slate-600 dark:text-slate-300">{intro}</p>}
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Actualizado el <time dateTime={LEGAL.fechaISO}>{LEGAL.ultimaActualizacion}</time>
        </p>
      </header>

      <div className="mt-8 lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-12">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav aria-label="Documentos legales" className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] lg:mx-0 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
            <ul className="flex gap-1 border-b border-slate-200 dark:border-white/10 lg:flex-col lg:gap-0 lg:border-b-0 lg:border-l">
              {LEGAL_PAGES.map((p) => (
                <li key={p.to}>
                  <NavLink
                    to={p.to}
                    className={({ isActive }) => `-mb-px block whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors lg:-mb-0 lg:-ml-px lg:border-b-0 lg:border-l-2 lg:py-1.5 lg:pl-4 ${
                      isActive
                        ? 'border-amber-500 font-semibold text-slate-900 dark:border-yellow-400 dark:text-white'
                        : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                    }`}
                  >
                    {p.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="En esta página" className="mt-8 hidden lg:block">
            <p className="text-xs font-semibold text-slate-900 dark:text-white">En esta página</p>
            <ol className="mt-3 space-y-1.5 text-sm">
              {secciones.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => { e.preventDefault(); irASeccion(s.id); }}
                    aria-current={activa === s.id ? 'location' : undefined}
                    className={`block leading-snug transition-colors ${activa === s.id ? 'text-slate-900 dark:text-white' : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'}`}
                  >
                    {s.titulo}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <article className="mt-6 min-w-0 rounded-2xl border border-slate-200/80 bg-white/85 px-5 py-7 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/70 sm:px-8 sm:py-9 lg:mt-0 lg:px-10">
          {resumen && <div className="mb-9">{resumen}</div>}
          {secciones.map((s, i) => (
            <section key={s.id} id={s.id} aria-labelledby={`${s.id}-titulo`} className={`scroll-mt-24 ${i > 0 ? 'mt-9 border-t border-slate-200 pt-9 dark:border-white/10' : ''}`}>
              <h2 id={`${s.id}-titulo`} className="flex items-baseline gap-3 text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                <span aria-hidden="true" className="text-sm font-semibold tabular-nums text-slate-400 dark:text-slate-500">{String(i + 1).padStart(2, '0')}</span>
                {s.titulo}
              </h2>
              <div className={`mt-3 ${prosa}`}>{s.contenido}</div>
            </section>
          ))}
        </article>
      </div>
    </div>
  );
};

export default LegalDocument;
